<template>
  <div class="fa-page">
    <q-linear-progress
      v-if="this.isFetching"
      indeterminate
      color="primary"
      class="fa-page__progress"
    />

    <div class="fa-page__header">
      <PageTitle>Принятие работ НУ</PageTitle>
      <div class="fa-page__header-actions">
        <q-btn flat round icon="refresh" color="primary" :loading="this.isFetching" @click="this.loadList()" />
        <q-btn
          flat
          round
          icon="refresh"
          color="primary"
          :loading="this.isFetching"
          @click="this.loadList"
        />
      </div>
    </div>

    <!-- ===== ФИЛЬТРЫ ===== -->
    <div class="fa-filters">
      <div class="fa-filters__item fa-filters__item--wide">
        <div class="fa-filters__label">Проект УСП</div>
        <q-input
          :model-value="this.projectLabel"
          readonly
          outlined
          dense
          placeholder="Выберите..."
          clearable
          @clear="this.clearProject"
        >
          <template #prepend>
            <q-btn icon="list" flat dense round @click="this.openUspDialog">
              <q-tooltip>Выбрать проект УСП</q-tooltip>
            </q-btn>
          </template>
        </q-input>
      </div>

      <div class="fa-filters__item">
        <div class="fa-filters__label">Блок</div>
        <q-input
          :model-value="this.entranceLabel"
          readonly
          outlined
          dense
          placeholder="Выберите..."
          :disable="!this.filters.sbs_project_id"
          clearable
          @clear="this.clearEntrance"
        >
          <template #prepend>
            <q-btn
              icon="account_tree"
              flat
              dense
              round
              :disable="!this.filters.sbs_project_id"
              @click="this.openEntranceTreeDialog"
            >
              <q-tooltip>Выбрать блок</q-tooltip>
            </q-btn>
          </template>
        </q-input>
      </div>

      <div class="fa-filters__item fa-filters__item--narrow">
        <div class="fa-filters__label">Этаж</div>
        <CustomSelect
          :options="this.floorsOptions"
          option-label-key="level_label"
          option-value-key="floor_map_id"
          :value="this.filters.floor_map_id"
          :disable="!this.floorFilterAvailable"
          :loading="this.floorsLoading"
          @change="(v) => this.onFilter('floor_map_id', v)"
        >
          <q-tooltip
            v-if="this.filters.project_entrance_id && !this.floorFilterAvailable"
          >
            Выберите блок для фильтра по этажу
          </q-tooltip>
        </CustomSelect>
      </div>

      <div class="fa-filters__item">
        <div class="fa-filters__label">Тип проекта</div>
        <CustomSelect
          :options="this.projectTypeOptions"
          option-label-key="project_type_name"
          option-value-key="project_type_id"
          :value="this.filters.project_type_id"
          :loading="this.projectTypesLoading"
          @change="(v) => this.onFilter('project_type_id', v)" />
      </div>

      <div class="fa-filters__item">
        <div class="fa-filters__label">Контрагент</div>
        <CustomSelect
          :options="this.contractorOptions"
          option-label-key="contractor_name"
          option-value-key="contractor_id"
          :value="this.filters.contractor_id"
          :loading="this.contractorsLoading"
          @change="(v) => this.onFilter('contractor_id', v)" />
      </div>

      <div class="fa-filters__item fa-filters__item--narrow">
        <div class="fa-filters__label">Статус НУ</div>
        <CustomSelect
          :options="this.foremanStatusOptions"
          option-label-key="label"
          option-value-key="value"
          :value="this.filters.is_foreman_checked"
          @change="(v) => this.onFilter('is_foreman_checked', v)"
        />
      </div>

      <div class="fa-filters__item fa-filters__item--btn">
        <div class="fa-filters__label fa-filters__label--spacer">_</div>
        <q-btn
          flat
          dense
          icon="filter_alt_off"
          color="grey-7"
          label="Сбросить"
          class="fa-filters__btn"
          @click="this.resetFilters"
        />
      </div>
    </div>

    <!-- ===== СЧЁТЧИК СТРОК ===== -->
    <div v-if="!this.loading" class="fa-row-count">
      <span v-if="this.total > 0">
        Найдено записей: <strong>{{ this.total }}</strong>
      </span>
      <span v-else class="text-grey-6">Нет записей</span>
    </div>

    <!-- ===== ТАБЛИЦА ===== -->
    <q-table
      :rows="this.listData"
      :columns="this.tableColumns"
      :loading="this.loading"
      row-key="help_call_id"
      flat
      dense
      wrap-cells
      :rows-per-page-options="[0]"
      no-data-label="Нет данных"
    >
      <!-- Проект / Блок / Этаж / Группа работ -->
      <template #body-cell-location="props">
        <q-td :props="props">
          <div class="fa-cell-location">
            <span v-if="props.row.project_name" class="fa-location-project">
              <q-icon name="domain" size="13px" class="q-mr-xs" />
              {{ props.row.project_name }}
            </span>
            <span class="fa-location-block">
              <q-icon name="apartment" size="13px" class="q-mr-xs" />
              {{ props.row.block_name || '—' }}
              <a
                v-if="props.row.level_num && props.row.floor_map_id"
                class="fa-location-floor-link"
                :href="floorLink(props.row)"
                target="_blank"
                @click.stop
              >
                · эт. {{ props.row.level_num }}
              </a>
              <span v-else-if="props.row.level_num">
                · эт. {{ props.row.level_num }}</span
              >
            </span>
          </div>
        </q-td>
      </template>

      <!-- Тип проекта -->
      <template #body-cell-project_type="props">
        <q-td :props="props">
          <span v-if="props.row.project_type_name" class="fa-cell-meta">
            {{ props.row.project_type_name }}
          </span>
          <span v-else class="text-grey-5">—</span>
        </q-td>
      </template>

      <!-- Контрагент -->
      <template #body-cell-contractor="props">
        <q-td :props="props">
          <span v-if="props.row.contractor_name" class="fa-cell-meta">
            {{ props.row.contractor_name }}
          </span>
          <span v-else class="text-grey-5">—</span>
        </q-td>
      </template>

      <!-- Группа работ -->
      <template #body-cell-work_info="props">
        <q-td :props="props">
          <div class="fa-cell-work">
            <span
              class="fa-work-group"
              >{{ props.row.work_set_check_group_name }}</span
            >
            <div v-if="props.row.items_count > 0" class="fa-works-badge-wrap">
              <q-btn
                round
                unelevated
                size="xs"
                color="primary"
                class="fa-works-badge"
                @click.stop="openWorksModal(props.row)"
              >
                {{ props.row.items_count }}
                <q-tooltip
                  >{{ props.row.items_count }}
                  {{ workCountLabel(props.row.items_count) }} — нажмите для
                  просмотра</q-tooltip
                >
              </q-btn>
              <span class="fa-works-badge-label">работ</span>
            </div>
          </div>
        </q-td>
      </template>

      <!-- Сумма -->
      <template #body-cell-call_sum="props">
        <q-td :props="props" class="text-right">
          <span v-if="props.row.call_sum" class="fa-sum">
            {{ Number(props.row.call_sum).toLocaleString('ru-RU') }} ₸
          </span>
          <span v-else class="text-grey-5">—</span>
        </q-td>
      </template>

      <!-- ДТН: вызов / принятие -->
      <template #body-cell-dtn_status="props">
        <q-td :props="props">
          <div class="fa-check-block">
            <div class="fa-check-row">
              <span class="fa-check-label">Вызов:</span>
              <span
                class="fa-check-value"
                >{{ props.row.call_date || '—' }}</span
              >
            </div>
            <div class="fa-check-row">
              <span class="fa-check-label">Принял:</span>
              <div
                v-if="props.row.is_checked"
                class="fa-inline-badge fa-inline-badge--ok"
              >
                <q-icon name="check_circle" size="12px" />
                {{ props.row.check_date || '—' }}
              </div>
              <span v-else class="fa-check-value text-grey-6">
                <q-icon name="hourglass_empty" size="12px" />
                Не принято
              </span>
            </div>
          </div>
        </q-td>
      </template>

      <!-- НУ: вызов / принятие / кнопка Принять -->
      <template #body-cell-foreman_status="props">
        <q-td :props="props">
          <div class="fa-check-block">
            <div class="fa-check-row">
              <span class="fa-check-label">Вызов:</span>
              <span
                class="fa-check-value"
                >{{ props.row.foreman_call_date || '—' }}</span
              >
            </div>
            <div class="fa-check-row">
              <span class="fa-check-label">Принял:</span>
              <div
                v-if="props.row.is_foreman_checked"
                class="fa-inline-badge fa-inline-badge--ok"
              >
                <q-icon name="check_circle" size="12px" />
                {{ props.row.foreman_check_date || '—' }}
              </div>
              <div
                v-else
                class="fa-inline-badge fa-inline-badge--wait fa-inline-badge--clickable"
                @click.stop="onAccept(props.row)"
              >
                <q-spinner
                  v-if="!!acceptingIds[props.row.help_call_id]"
                  size="11px"
                />
                <q-icon v-else name="hourglass_empty" size="12px" />
                Не принято
                <q-tooltip>Нажмите, чтобы принять</q-tooltip>
              </div>
            </div>
          </div>
        </q-td>
      </template>

      <!-- Комментарий -->
      <template #body-cell-comment="props">
        <q-td :props="props">
          <span
            v-if="props.row.comment"
            class="fa-comment"
            >{{ props.row.comment }}</span
          >
          <span v-else class="text-grey-5">—</span>
        </q-td>
      </template>
    </q-table>

    <!-- ===== ПАГИНАЦИЯ ===== -->
    <div v-if="this.pages > 1" class="fa-pagination">
      <q-pagination
        :model-value="this.page"
        :max="this.pages"
        max-pages="7"
        color="grey-8"
        size="sm"
        @update:model-value="this.onPageChange"
      />
    </div>

    <!-- ===== МОДАЛКА: СПИСОК РАБОТ ===== -->
    <q-dialog v-model="this.worksModal.show">
      <q-card style="width:700px; max-width:90vw;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-subtitle1 text-weight-medium">
            Работы — {{ this.worksModal.group_name }}
          </div>
          <q-space />
          <q-btn
            icon="close"
            flat
            round
            dense
            @click="this.worksModal.show = false"
          />
        </q-card-section>

        <q-card-section>
          <q-linear-progress
            v-if="this.worksModal.loading"
            indeterminate
            class="q-mb-sm"
          />

          <q-table
            v-if="!this.worksModal.loading"
            :rows="this.worksModal.items"
            :columns="this.worksModalColumns"
            row-key="work_set_id"
            flat
            dense
            wrap-cells
            :rows-per-page-options="[0]"
            no-data-label="Нет работ"
          >
            <template #body-cell-work_set_name="props">
              <q-td
                :props="props"
                style="white-space:normal; word-break:break-word; min-width:200px;"
              >
                {{ props.row.work_set_name }}
              </q-td>
            </template>
            <template #body-cell-work_set_amount="props">
              <q-td :props="props" class="text-right">
                {{ props.row.work_set_amount != null ? Number(props.row.work_set_amount).toFixed(2) : '—' }}
              </q-td>
            </template>
            <template #body-cell-call_sum="props">
              <q-td :props="props" class="text-right">
                <span v-if="props.row.call_sum">
                  {{ Number(props.row.call_sum).toLocaleString('ru-RU') }} ₸
                </span>
                <span v-else class="text-grey-5">—</span>
              </q-td>
            </template>
          </q-table>
        </q-card-section>

        <q-card-section v-if="this.worksModal.total_sum" class="q-pt-none">
          <div class="text-right text-weight-medium">
            Итого:
            {{ Number(this.worksModal.total_sum).toLocaleString('ru-RU') }} ₸
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>

    <q-dialog v-model="showNewTaskDialog">
      <q-card class="work-sets-okkerr-dialog" style="min-width: 500px; max-width: 700px">
        <q-card-section>
          <div class="text-h6">Новая задача!</div>
        </q-card-section>
        <q-card-section class="q-pt-none">
          <div class="work-sets-okkerr-dialog__message tw-whitespace-pre-wrap">
            {{ 'У вас появилась новая задача, выполните ее' }}
          </div>
        </q-card-section>
        <q-separator />
        <q-card-actions
          class="q-pa-md"
          align="right"
          style="flex-wrap: wrap; gap: 8px; justify-content: flex-end"
        >
          <q-btn flat no-caps label="Закрыть" v-close-popup />
          <q-btn
            color="primary"
            no-caps
            label="Мои задачи"
            @click="goToEmployeeTasks"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- ===== ДИАЛОГ ПОДТВЕРЖДЕНИЯ ПРИНЯТИЯ ===== -->
    <q-dialog v-model="confirmDialog.show" persistent>
      <q-card style="width:480px; max-width:95vw;">
        <q-card-section class="row items-center q-pb-sm">
          <q-icon
            name="help_outline"
            color="warning"
            size="24px"
            class="q-mr-sm"
          />
          <span class="text-subtitle1 text-weight-medium"
            >Подтверждение принятия</span
          >
        </q-card-section>

        <q-separator />

        <q-card-section class="q-pt-md">
          <p class="q-mb-sm">
            Вы действительно хотите принять работы
            <strong>{{ confirmDialog.row?.work_set_check_group_name }}</strong
            >?
          </p>
          <div v-if="confirmPaymentSum" class="fa-confirm-payment-info">
            <q-icon name="payments" size="16px" class="q-mr-xs" />
            Сумма работ:
            <strong>{{ confirmPaymentSum.toLocaleString('ru-RU') }} ₸</strong>
            <div class="text-grey-7 q-mt-xs" style="font-size:12px;">
              Оплата будет сформирована за минусом процентов и гарантии
            </div>
          </div>
        </q-card-section>

        <q-card-actions align="right" class="q-pt-none q-pb-md q-pr-md">
          <q-btn
            flat
            no-caps
            label="Отмена"
            color="grey-7"
            @click="confirmDialog.show = false"
          />
          <q-btn
            unelevated
            :loading="isAccepting"
            no-caps
            label="Принять"
            color="positive"
            @click="onAcceptConfirm"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- ===== ДИАЛОГ ВЫБОРА ПОДЪЕЗДА ===== -->
    <q-dialog v-model="this.entranceTreeDialog.show" persistent>
      <q-card style="min-width:500px; max-width:660px; width:100%;">
        <q-card-section class="row items-center q-pb-xs">
          <div class="text-h6">Выбор блока</div>
          <q-space />
          <q-btn
            flat
            dense
            no-caps
            size="sm"
            color="primary"
            :icon="this.entranceTreeDialog.allExpanded ? 'unfold_less' : 'unfold_more'"
            :label="this.entranceTreeDialog.allExpanded ? 'Свернуть все' : 'Развернуть все'"
            class="q-mr-sm"
            @click="this.toggleTreeExpand"
          />
          <q-btn
            icon="close"
            flat
            round
            dense
            @click="this.entranceTreeDialog.show = false"
          />
        </q-card-section>

        <q-card-section
          style="max-height:460px; overflow-y:auto;"
          class="q-pt-xs q-pb-xs"
        >
          <q-linear-progress
            v-if="this.entranceTreeDialog.loading"
            indeterminate
            class="q-mb-sm"
          />

          <div
            v-if="!this.entranceTreeDialog.loading && this.entranceTreeNodes.length === 0"
            class="text-grey-5 text-center q-pa-lg"
          >
            <q-icon name="folder_off" size="40px" />
            <div class="q-mt-sm">Блоки не найдены</div>
          </div>

          <q-tree
            v-else
            :nodes="this.entranceTreeNodes"
            node-key="id"
            tick-strategy="leaf"
            v-model:ticked="this.entranceTreeDialog.ticked"
            v-model:expanded="this.entranceTreeDialog.expanded"
            no-connectors
            dense
          >
            <template #default-header="prop">
              <div
                :class="['entrance-tree-node',
                prop.node.children ? 'entrance-tree-node--project' : 'entrance-tree-node--entrance',
                this.entranceTreeDialog.ticked.includes(prop.node.id) ? 'entrance-tree-node--selected' : '']"
              >
                <q-icon :name="prop.node.icon" size="15px" class="q-mr-xs" />
                <span>{{ prop.node.label }}</span>
              </div>
            </template>
          </q-tree>
        </q-card-section>

        <q-card-actions align="right" class="q-pt-xs">
          <span
            v-if="this.entranceTreeDialog.ticked.length > 0"
            class="text-caption text-grey-7 q-mr-sm"
          >
            Выбрано:
            {{ this.entranceTreeDialog.ticked.filter(k => k.startsWith('ent_')).length }}
          </span>
          <q-btn
            flat
            label="Отмена"
            color="grey"
            @click="this.entranceTreeDialog.show = false"
          />
          <q-btn
            unelevated
            label="Применить"
            color="primary"
            @click="this.applyEntranceSelection"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- ===== ДИАЛОГ ВЫБОРА ПРОЕКТА УСП ===== -->
    <q-dialog v-model="this.uspDialog.show" persistent>
      <q-card style="min-width:800px; max-width:1000px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Выбор проекта УСП</div>
          <q-space />
          <q-btn
            icon="close"
            flat
            round
            dense
            @click="this.uspDialog.show = false"
          />
        </q-card-section>

        <q-card-section>
          <q-input
            v-model="this.uspDialog.search"
            outlined
            dense
            clearable
            autofocus
            placeholder="Поиск по названию проекта или типу..."
          >
            <template #prepend>
              <q-icon name="search" />
            </template>
          </q-input>

          <div style="margin-top:16px;">
            <q-linear-progress v-if="this.uspDialog.loading" indeterminate />

            <div
              v-else-if="this.uspFilteredRows.length === 0"
              style="text-align:center;padding:40px;color:#999;"
            >
              <q-icon name="folder_off" size="48px" />
              <div style="margin-top:12px;">Проекты не найдены</div>
            </div>

            <q-table
              v-else
              :rows="this.uspFilteredRows"
              :columns="this.uspColumns"
              row-key="project_group_id"
              flat
              bordered
              :rows-per-page-options="[10, 20, 50]"
              :pagination="{ rowsPerPage: 10 }"
            >
              <template #body-cell-project_guid="props">
                <q-td :props="props">
                  <div style="font-family:monospace;font-size:11px;color:#666;">
                    {{ props.value }}
                  </div>
                </q-td>
              </template>
              <template #body-cell-actions="props">
                <q-td :props="props">
                  <q-btn
                    label="Выбрать"
                    color="primary"
                    size="sm"
                    unelevated
                    @click="this.onUspProjectSelect(props.row)"
                  />
                </q-td>
              </template>
            </q-table>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn
            flat
            label="Отмена"
            color="grey"
            @click="this.uspDialog.show = false"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import PageTitle from "@/components/common/PageTitle";
import CustomSelect from "@/components/form/CustomSelect";
import {
  signEntranceDocument,
} from "@/views/ResidentialSettings/services";
import { foremanAcceptanceAPI } from "./services/api";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";

const PAGE_SIZE = 50;

const FOREMAN_STATUS_OPTIONS = [
  { value: null,    label: "Все" },
  { value: "true",  label: "Принято НУ" },
  { value: "false", label: "Не принято" },
];

/**
 * Элементы asr_docs от GET /project/foreman-acceptance/ (при is_asr):
 * — floor_map_document_id или id — достаточно для POST /project/documents/sign/ (signEntranceDocument);
 * — floor_map_document_type_code — только для подписи в UI;
 * — is_nu_signed — если false, показываем кнопку «Подписать» при наличии id.
 * sign_asr_pre_call (floor_map) создаёт документ и подписывает — другой сценарий; для уже созданного документа нужен только floor_map_document_id.
 * asr_docs1 — документы пре-колл (в т.ч. после подписи подрядчика); asr_docs2 — второй контур (напр. ИС), может быть [].
 * Кнопка «Подписать» для НУ на том же floor_map_document_id: is_contractor_signed (или is_contrator_signed с бэка) и пустой asr_docs2.
 */
const FLOOR_MAP_DOCUMENT_TYPE_CODE_LABELS = {
  ASR_PRE_CALL: "Акт скрытых работ (до вызова ИТР)",
  ASR: "Акт скрытых работ",
  AVR: "Акт выполненных работ",
  EXEC_LIST_PRE_CALL: "Исполнительная схема (до вызова)",
  EXEC_LIST: "Исполнительная схема",
  K2SV: "К2-СВ",
  "3KC": "3-КС",
  BUH_AVR: "Бухгалтерский АВР",
  M19: "М-19",
  M29: "М-29",
  M19_OVER: "М-19 (перекрытие)",
};

export default {
  name: "ForemanWorkAcceptance",
  components: { PageTitle, CustomSelect },

  data() {
    return {
      foremanStatusOptions: FOREMAN_STATUS_OPTIONS,

      listData:    [],
      total:       0,
      page:        1,
      pages:       1,
      loading:     false,
      isFetching:  false,
      isAccepting: false,

      filters: {
        sbs_project_id:      null,
        project_entrance_id: null,
        floor_map_id:        null,
        project_type_id:     null,
        is_foreman_checked:  "false",
        contractor_id:       null,
      },

      projectLabel:  null,
      entranceLabel: null,

      floorsOptions: [],
      floorsLoading: false,

      projectTypeOptions: [],
      projectTypesLoading: false,

      contractorOptions: [],
      contractorsLoading: false,

      worksModal: {
        show:       false,
        loading:    false,
        group_name: null,
        items:      [],
        total_sum:  0,
      },

      acceptingIds: {},

      confirmDialog: {
        show: false,
        row:  null,
      },
      showNewTaskDialog: false,
      confirmSignDocLoadingId: null,

      entranceTreeDialog: {
        show:        false,
        loading:     false,
        rows:        [],
        ticked:      [],
        expanded:    [],
        allExpanded: true,
      },

      uspDialog: {
        show:    false,
        search:  "",
        loading: false,
      },
      uspAllProjects: [],
      uspColumns: [
        { name: "project_name", label: "Название проекта", field: "project_name", align: "left",   sortable: true },
        { name: "project_type", label: "Тип проекта",      field: "project_type", align: "left",   sortable: true },
        { name: "project_guid", label: "GUID",             field: "project_guid", align: "left",   sortable: false },
        { name: "actions",      label: "Действия",         field: "actions",      align: "center" },
      ],

      tableColumns: [
        { name: "location",       field: "location",       label: "Проект / Блок / Этаж", align: "left",   style: "width:18%" },
        { name: "project_type",   field: "project_type",   label: "Тип проекта",           align: "left",   style: "width:9%"  },
        { name: "contractor",     field: "contractor",     label: "Контрагент",            align: "left",   style: "width:11%" },
        { name: "work_info",      field: "work_info",      label: "Группа работ",          align: "left",   style: "width:14%" },
        { name: "call_sum",       field: "call_sum",       label: "Сумма",                 align: "right",  style: "width:8%"  },
        { name: "foreman_status", field: "foreman_status", label: "НУ",                    align: "left",   style: "width:15%" },
        { name: "dtn_status",     field: "dtn_status",     label: "ДТН",                   align: "left",   style: "width:15%" },
        { name: "comment",        field: "comment",        label: "Комментарий",           align: "left",   style: "width:10%" },
      ],

      worksModalColumns: [
        { name: "work_set_name",  field: "work_set_name",  label: "Наименование работы", align: "left",  style: "width:60%" },
        { name: "work_set_amount", field: "work_set_amount", label: "Объём",             align: "right", style: "width:15%" },
        { name: "call_sum",       field: "call_sum",        label: "Сумма",              align: "right", style: "width:25%" },
      ],
    };
  },

  computed: {
    confirmPaymentSum() {
      const sum = this.confirmDialog.row?.call_sum;
      if (!sum) return null;
      return Math.round(Number(sum));
    },

    uspFilteredRows() {
      if (!this.uspDialog.search) return this.uspAllProjects;
      const q = this.uspDialog.search.toLowerCase();
      return this.uspAllProjects.filter(
        (p) =>
          (p.project_name || "").toLowerCase().includes(q) ||
          (p.project_type || "").toLowerCase().includes(q)
      );
    },

    entranceTreeNodes() {
      const projectMap = {};
      for (const row of this.entranceTreeDialog.rows) {
        if (!projectMap[row.project_id]) {
          const typeStr = row.project_type_name ? ` (${row.project_type_name})` : "";
          projectMap[row.project_id] = {
            id:       `proj_${row.project_id}`,
            label:    `${row.resident_name}${typeStr} #${row.project_id}`,
            icon:     "apartment",
            children: [],
          };
        }
        projectMap[row.project_id].children.push({
          id:                  `ent_${row.project_entrance_id}`,
          label:               row.block_name,
          icon:                "door_front",
          project_entrance_id: row.project_entrance_id,
        });
      }
      return Object.values(projectMap);
    },

    entranceTreeAllProjectIds() {
      return this.entranceTreeNodes.map((n) => n.id);
    },

    floorFilterAvailable() {
      return !!this.filters.project_entrance_id;
    },
  },

  methods: {
   
    floorLink(row) {
      const p = new URLSearchParams({
        resident_id:         row.resident_id,
        project_type_id:     row.project_type_id,
        project_entrance_id: row.project_entrance_id,
        project_id:          row.project_id,
        sbs_project_id:      row.sbs_project_id,
        tab:                 'EntranceSchema',
        floor:               row.level_num,
        floor_map_id:        row.floor_map_id,
      });
      return `/residential-settings?${p.toString()}`;
    },

    workCountLabel(n) {
      if (n % 10 === 1 && n % 100 !== 11) return 'работа';
      if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100)) return 'работы';
      return 'работ';
    },
    async openWorksModal(row) {
      this.worksModal.group_name = row.work_set_check_group_name || `Вызов #${row.help_call_id}`;
      this.worksModal.items      = [];
      this.worksModal.total_sum  = 0;
      this.worksModal.show       = true;
      this.worksModal.loading    = true;
      try {
        const res = await foremanAcceptanceAPI.getItems(row.help_call_id);
        this.worksModal.items     = res?.data || [];
        this.worksModal.total_sum = this.worksModal.items.reduce((s, i) => s + (i.call_sum || 0), 0);
      } catch (e) {
        this.$q.notify({ type: "negative", message: e?.message || "Ошибка загрузки работ" });
      } finally {
        this.worksModal.loading = false;
      }
    },

    onAccept(row) {
      this.confirmDialog.row  = row;
      this.confirmDialog.show = true;
    },

    goToEmployeeTasks() {
      this.showNewTaskDialog = false;
      window.open("/employee-tasks", "_blank");
    },

    async onAcceptConfirm() {
      this.isAccepting = true;
      const row = this.confirmDialog.row;
      // this.confirmDialog.show = false;
      this.acceptingIds = { ...this.acceptingIds, [row.help_call_id]: true };
      try {
        await foremanAcceptanceAPI.accept(row.help_call_id);
        this.showNewTaskDialog = true;
        this.$q.notify({ type: "positive", message: "Работы приняты НУ" });
        await this.loadList();
        this.confirmDialog.show = false;
      } catch (e) {
      } finally {
        this.isAccepting = false;
        this.acceptingIds = { ...this.acceptingIds, [row.help_call_id]: false };
      }
    },
    onFilter(key, value) {
      this.filters[key] = value ?? null;
      if (key === "project_entrance_id") {
        this.filters.floor_map_id = null;
        this.floorsOptions = [];
        if (value) this.loadFloors(value);
      }
      this.loadList(1);
    },

    clearProject() {
      this.filters.sbs_project_id      = null;
      this.filters.project_entrance_id = null;
      this.filters.floor_map_id        = null;
      this.projectLabel                = null;
      this.entranceLabel               = null;
      this.floorsOptions               = [];
      this.entranceTreeDialog.rows     = [];
      this.entranceTreeDialog.ticked   = [];
      this.loadList(1);
    },

    clearEntrance() {
      this.filters.project_entrance_id = null;
      this.filters.floor_map_id        = null;
      this.entranceLabel               = null;
      this.floorsOptions               = [];
      this.entranceTreeDialog.ticked   = [];
      this.loadList(1);
    },

    resetFilters() {
      this.filters = {
        sbs_project_id:      null,
        project_entrance_id: null,
        floor_map_id:        null,
        project_type_id:     null,
        is_foreman_checked:  "false",
        contractor_id:       null,
      };
      this.projectLabel              = null;
      this.entranceLabel             = null;
      this.floorsOptions             = [];
      this.entranceTreeDialog.rows   = [];
      this.entranceTreeDialog.ticked = [];
      this.loadList(1);
    },

    onPageChange(page) {
      this.page = page;
      this.loadList(page);
    },

    async loadList(page) {
      if (page !== undefined) this.page = page;
      this.loading    = true;
      this.isFetching = true;
      try {
        const res = await foremanAcceptanceAPI.getList({
          ...this.filters,
          page:  this.page,
          limit: PAGE_SIZE,
        });
        this.listData = res?.data        || [];
        this.total    = parseInt(res?.total_count) || 0;
        this.pages    = Math.ceil(this.total / PAGE_SIZE) || 1;
      } catch (e) {
        this.$q.notify({ type: "negative", message: e?.message || "Ошибка загрузки данных" });
      } finally {
        this.loading    = false;
        this.isFetching = false;
      }
    },

    async loadFloors(project_entrance_id) {
      this.floorsLoading = true;
      try {
        const res = await foremanAcceptanceAPI.getFloors(project_entrance_id);
        this.floorsOptions = res?.data || res || [];
      } catch (e) {
        console.error("Ошибка загрузки этажей:", e);
      } finally {
        this.floorsLoading = false;
      }
    },

    // ── Типы проекта ──────────────────────────────────────────────────────
    async loadProjectTypes() {
      this.projectTypesLoading = true;
      try {
        const res = await foremanAcceptanceAPI.getProjectTypes();
        const list = res?.data || res || [];
        this.projectTypeOptions = [
          { project_type_id: null, project_type_name: "Все" },
          ...list,
        ];
      } catch (e) {
        console.error("Ошибка загрузки типов проекта:", e);
      } finally {
        this.projectTypesLoading = false;
      }
    },

    // ── Контрагенты ───────────────────────────────────────────────────────
    async loadContractors() {
      this.contractorsLoading = true;
      try {
        const res = await foremanAcceptanceAPI.getContractors();
        const list = res?.data || res || [];
        this.contractorOptions = [
          { contractor_id: null, contractor_name: "Все" },
          ...list,
        ];
      } catch (e) {
        console.error("Ошибка загрузки контрагентов:", e);
      } finally {
        this.contractorsLoading = false;
      }
    },

    // ── Диалог USP проекта ────────────────────────────────────────────────
    async openUspDialog() {
      this.uspDialog.search = "";
      this.uspDialog.show   = true;
      if (this.uspAllProjects.length === 0) {
        this.uspDialog.loading = true;
        try {
          const res = await foremanAcceptanceAPI.getUspProjects();
          this.uspAllProjects = res?.data || res || [];
        } catch (e) {
          this.$q.notify({ type: "negative", message: "Ошибка загрузки проектов УСП" });
        } finally {
          this.uspDialog.loading = false;
        }
      }
    },

    onUspProjectSelect(row) {
      const label = row.project_name + (row.project_type ? ` (${row.project_type})` : "");
      this.filters.sbs_project_id      = row.sbs_project_id ?? null;
      this.filters.project_entrance_id = null;
      this.filters.floor_map_id        = null;
      this.projectLabel                = label;
      this.entranceLabel               = null;
      this.floorsOptions               = [];
      this.entranceTreeDialog.rows     = [];
      this.entranceTreeDialog.ticked   = [];
      this.uspDialog.show              = false;
      this.loadList(1);
    },

    // ── Диалог дерева блоков ──────────────────────────────────────────────
    async openEntranceTreeDialog() {
      if (!this.filters.sbs_project_id) {
        this.$q.notify({ type: "warning", message: "Сначала выберите проект УСП" });
        return;
      }
      this.entranceTreeDialog.show   = true;
      this.entranceTreeDialog.ticked = this.filters.project_entrance_id
        ? [`ent_${this.filters.project_entrance_id}`]
        : [];
      if (this.entranceTreeDialog.rows.length === 0) {
        await this.loadEntrancesTree();
      } else {
        this._syncTreeExpanded();
      }
    },

    async loadEntrancesTree() {
      this.entranceTreeDialog.loading = true;
      try {
        const res = await foremanAcceptanceAPI.getEntrancesTree(this.filters.sbs_project_id);
        this.entranceTreeDialog.rows = res?.data || res || [];
        this._syncTreeExpanded();
      } catch (e) {
        this.$q.notify({ type: "negative", message: e?.message || "Ошибка загрузки блоков" });
      } finally {
        this.entranceTreeDialog.loading = false;
      }
    },

    _syncTreeExpanded() {
      this.entranceTreeDialog.expanded    = [...this.entranceTreeAllProjectIds];
      this.entranceTreeDialog.allExpanded = true;
    },

    toggleTreeExpand() {
      if (this.entranceTreeDialog.allExpanded) {
        this.entranceTreeDialog.expanded    = [];
        this.entranceTreeDialog.allExpanded = false;
      } else {
        this.entranceTreeDialog.expanded    = [...this.entranceTreeAllProjectIds];
        this.entranceTreeDialog.allExpanded = true;
      }
    },

    applyEntranceSelection() {
      const ticked = this.entranceTreeDialog.ticked.filter((k) => k.startsWith("ent_"));
      if (ticked.length === 0) {
        this.clearEntrance();
        this.entranceTreeDialog.show = false;
        return;
      }
      const id = parseInt(ticked[0].replace("ent_", ""));
      const row = this.entranceTreeDialog.rows.find((r) => r.project_entrance_id === id);

      this.filters.project_entrance_id = id;
      this.filters.floor_map_id        = null;
      this.floorsOptions               = [];
      this.entranceLabel               = row ? row.block_name : `Блок ${id}`;
      this.entranceTreeDialog.show     = false;
      this.loadFloors(id);
      this.loadList(1);
    },
  },
  mounted() {
    this.loadList(1);
    this.loadProjectTypes();
    this.loadContractors();
  },
  setup() {
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar, showErrorSnackbar };
  },
};
</script>

<style scoped>
.fa-page {
  padding: 16px;
  position: relative;
}
.fa-page__progress {
  position: absolute;
  top: 0; left: 0; right: 0;
}
.fa-page__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.fa-page__header-actions {
  display: flex;
  gap: 4px;
}

.fa-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-end;
  margin-bottom: 12px;
}
.fa-filters__item {
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.fa-filters__item :deep(.q-field) {
  width: 100%;
}
.fa-filters__item :deep(.custom-select__wrapper) {
  width: 100%;
}
.fa-filters__item--wide {
  min-width: 280px;
}
.fa-filters__item--narrow {
  min-width: 150px;
}
.fa-filters__item--btn {
  min-width: auto;
}
.fa-filters__label {
  font-size: 13px;
  line-height: 13px;
  color: #1d1d1d;
}
.fa-filters__label--spacer {
  visibility: hidden;
}
.fa-filters__btn {
  align-self: center;
}

.fa-cell-location {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;
}
.fa-location-project {
  font-weight: 600;
  color: #1a237e;
  font-size: 12px;
}
.fa-location-block {
  font-weight: 500;
  color: #333;
}
.fa-location-floor-link {
  font-weight: 500;
  color: var(--q-primary);
  text-decoration: none;
  cursor: pointer;
}
.fa-location-floor-link:hover {
  text-decoration: underline;
}
.fa-location-remont {
  font-size: 11px;
}

.fa-cell-work {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
}
.fa-work-group {
  font-weight: 500;
}
.fa-process-step {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 8px;
}
.fa-process-step__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.fa-process-step__title {
  font-size: 12px;
  color: #1f2937;
  line-height: 1.2;
}
.fa-process-step__message {
  font-size: 11px;
  color: #64748b;
  line-height: 1.2;
}
.fa-works-badge-wrap {
  display: flex;
  align-items: center;
  gap: 5px;
}
.fa-works-badge {
  font-size: 11px;
  font-weight: 600;
  min-width: 22px;
  height: 22px;
}
.fa-works-badge-label {
  font-size: 11px;
  color: #666;
}

.fa-sum {
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.fa-check-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
}
.fa-check-row {
  display: flex;
  align-items: center;
  gap: 4px;
}
.fa-check-label {
  color: #888;
  font-size: 11px;
  min-width: 42px;
}
.fa-check-value {
  font-size: 11px;
}
.fa-inline-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
  white-space: nowrap;
}
.fa-inline-badge--ok {
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
}
.fa-inline-badge--wait {
  background: #fff8e1;
  color: #7a6200;
  border: 1px solid #ffe082;
}
.fa-inline-badge--clickable {
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.fa-inline-badge--clickable:hover {
  background: #e8f5e9;
  color: #2e7d32;
  border-color: #a5d6a7;
}

.fa-comment {
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}

.fa-row-count {
  font-size: 13px;
  color: #555;
  margin-bottom: 6px;
}

.fa-confirm-payment-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 13px;
  color: #2e7d32;
  margin-top: 8px;
}

.fa-confirm-asr-banner {
  background: #fff8e1;
  border: 1px solid #ffcc80;
  color: #5d4037;
  font-size: 13px;
}
.fa-confirm-asr-list li {
  margin-bottom: 6px;
}
.fa-confirm-asr-list__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  list-style: disc;
}
.fa-confirm-asr-list__label {
  flex: 1 1 auto;
  min-width: 0;
}
.fa-confirm-asr-list__sign-btn {
  flex-shrink: 0;
}

.fa-pagination {
  display: flex;
  justify-content: center;
  padding: 12px 0;
}


.entrance-tree-node {
  display: flex;
  align-items: center;
  font-size: 13px;
  padding: 2px 0;
}
.entrance-tree-node--project {
  font-weight: 600;
}
.entrance-tree-node--entrance {
  color: #555;
}
.entrance-tree-node--selected {
  color: var(--q-primary);
}

.fa-cell-meta {
  font-size: 12px;
  color: #374151;
  line-height: 1.4;
}
</style>
